import { useMemo, useCallback } from 'react';
import {
  createTimeRange,
  getPositionInDay,
  isValidTimeFormat,
} from '../../../../domain/doctorShift';

/**
 * タイムライン表示設定
 */
export interface TimelineConfig {
  /** 1日の開始時間（例: 7 = 07:00） */
  dayStartHour: number;
  /** 1日の終了時間（例: 22 = 22:00） */
  dayEndHour: number;
}

/**
 * タイムラインポジション計算結果
 */
export interface TimelinePosition {
  /** 開始位置（パーセント） */
  left: number;
  /** 幅（パーセント） */
  width: number;
}

/**
 * useTimelinePosition フックの戻り値
 */
export interface UseTimelinePositionReturn {
  /** デフォルト設定 */
  config: TimelineConfig;
  /** 位置計算関数 */
  calculatePosition: (startTime: string, endTime: string) => TimelinePosition;
  /** 時間軸ラベル生成 */
  getTimeLabels: () => string[];
  /** 分単位での総幅 */
  totalMinutes: number;
  /** ピクセル換算（1分あたりのピクセル数計算用） */
  getMinutesWidth: (minutes: number, containerWidth: number) => number;
}

/**
 * デフォルトのタイムライン設定
 */
const DEFAULT_CONFIG: TimelineConfig = {
  dayStartHour: 7,
  dayEndHour: 22,
};

/**
 * タイムライン位置計算フック
 *
 * @description
 * シフトバーの位置・幅計算ロジックを提供する。
 * Domain層のTimeRangeを活用して計算する。
 *
 * @param {TimelineConfig} [config] - タイムライン設定
 * @returns {UseTimelinePositionReturn} 位置計算関数群
 *
 * @example
 * const { calculatePosition, getTimeLabels } = useTimelinePosition();
 * const position = calculatePosition('08:00', '12:00');
 * // { left: 6.67, width: 26.67 }
 */
export const useTimelinePosition = (
  config: TimelineConfig = DEFAULT_CONFIG
): UseTimelinePositionReturn => {
  const totalMinutes = useMemo(
    () => (config.dayEndHour - config.dayStartHour) * 60,
    [config.dayStartHour, config.dayEndHour]
  );

  /**
   * 開始・終了時間からタイムライン上の位置を計算
   */
  const calculatePosition = useCallback(
    (startTime: string, endTime: string): TimelinePosition => {
      // 時間形式のバリデーション
      if (!isValidTimeFormat(startTime) || !isValidTimeFormat(endTime)) {
        return { left: 0, width: 0 };
      }

      try {
        const range = createTimeRange(startTime, endTime);
        const { startPercent, widthPercent } = getPositionInDay(
          range,
          config.dayStartHour,
          config.dayEndHour
        );

        return {
          left: startPercent,
          width: widthPercent,
        };
      } catch {
        return { left: 0, width: 0 };
      }
    },
    [config.dayStartHour, config.dayEndHour]
  );

  /**
   * 時間軸ラベルを生成（07:00, 08:00, ... 22:00）
   */
  const getTimeLabels = useCallback((): string[] => {
    const labels: string[] = [];
    for (let hour = config.dayStartHour; hour <= config.dayEndHour; hour++) {
      labels.push(`${hour.toString().padStart(2, '0')}:00`);
    }
    return labels;
  }, [config.dayStartHour, config.dayEndHour]);

  /**
   * 分数からコンテナ幅に対するピクセル幅を計算
   */
  const getMinutesWidth = useCallback(
    (minutes: number, containerWidth: number): number => {
      return (minutes / totalMinutes) * containerWidth;
    },
    [totalMinutes]
  );

  return {
    config,
    calculatePosition,
    getTimeLabels,
    totalMinutes,
    getMinutesWidth,
  };
};
